/**
 * Retry Banner Component
 * Error state with retry action following Polaris patterns
 *
 * Displays when data fetch fails, providing:
 * - Clear error message
 * - Actionable retry button
 * - Polaris-compliant styling
 */

import { Banner, Button } from '@shopify/polaris';

interface RetryBannerProps {
  /**
   * Error message to display
   * @default 'Failed to load data'
   */
  message?: string;

  /**
   * Callback when retry button is clicked
   */
  onRetry: () => void;

  /**
   * Whether retry is currently in progress
   * @default false
   */
  isRetrying?: boolean;
}

/**
 * Error banner with retry action
 * Follows Polaris error message guidelines:
 * - Be specific about what went wrong
 * - Be actionable with clear next steps
 * - Be concise without over-apologizing
 *
 * @example
 * ```tsx
 * {isError && (
 *   <RetryBanner
 *     message="Unable to load alerts. Check your connection."
 *     onRetry={retry}
 *     isRetrying={isRefreshing}
 *   />
 * )}
 * ```
 */
export function RetryBanner({
  message = 'Failed to load data',
  onRetry,
  isRetrying = false,
}: RetryBannerProps) {
  return (
    <Banner
      title="Something went wrong"
      tone="critical"
      action={{
        content: isRetrying ? 'Retrying...' : 'Try again',
        onAction: onRetry,
        loading: isRetrying,
        disabled: isRetrying,
      }}
    >
      <p>{message}</p>
    </Banner>
  );
}

export type { RetryBannerProps };
